import { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, ShowerHead, Container, PlugZap, MessageCircle, Power } from "lucide-react";

const ICONS = {
  "shower-head": ShowerHead,
  "container": Container,
  "plug-zap": PlugZap,
  "power": Power,
};
const ICON_OPTIONS = ["shower-head", "container", "plug-zap", "power"];

const emptyDraft = {
  slug: "", name: "", description: "", price_per_month: "",
  billing_type: "monthly", icon: "shower-head", active: true, sort_order: 99,
};

export default function ServicesEditor() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [savingId, setSavingId] = useState(null);

  const reload = () => api.get("/admin/services").then((r) => setServices(r.data));

  useEffect(() => {
    reload().catch(() => toast.error("Could not load services")).finally(() => setLoading(false));
  }, []);

  const updateField = (id, key, value) => {
    setServices((prev) => prev.map((s) => s.id === id ? { ...s, [key]: value, _dirty: true } : s));
  };

  const saveService = async (svc) => {
    setSavingId(svc.id);
    try {
      const payload = {
        name: svc.name,
        description: svc.description,
        price_per_month: svc.billing_type === "on_request" ? null : parseFloat(svc.price_per_month || 0),
        billing_type: svc.billing_type,
        icon: svc.icon,
        active: !!svc.active,
        sort_order: parseInt(svc.sort_order || 0),
      };
      const { data } = await api.patch(`/admin/services/${svc.id}`, payload);
      setServices((prev) => prev.map((s) => s.id === data.id ? data : s));
      toast.success(`${data.name} saved`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSavingId(null); }
  };

  const deleteService = async (svc) => {
    if (!window.confirm(`Delete "${svc.name}"? This won't affect existing bookings that already include it.`)) return;
    try {
      await api.delete(`/admin/services/${svc.id}`);
      setServices((prev) => prev.filter((s) => s.id !== svc.id));
      toast.success("Service deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const createService = async () => {
    if (!draft.slug.trim() || !draft.name.trim()) {
      toast.error("Slug and name are required");
      return;
    }
    setAdding(true);
    try {
      const payload = {
        ...draft,
        price_per_month: draft.billing_type === "on_request" ? null : parseFloat(draft.price_per_month || 0),
        sort_order: parseInt(draft.sort_order || 0),
      };
      const { data } = await api.post("/admin/services", payload);
      setServices((prev) => [...prev, data].sort((a, b) => a.sort_order - b.sort_order));
      setDraft(emptyDraft);
      toast.success("Service created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setAdding(false); }
  };

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0B0C0E]" /></div>;

  return (
    <div data-testid="services-editor" className="space-y-6">
      {/* Existing services */}
      <div className="border-2 border-[#0B0C0E] bg-white">
        <div className="border-b border-[#E5E3DA] px-6 py-4">
          <div className="overline">Add-on Services</div>
          <h2 className="mt-1 font-display font-black text-xl uppercase tracking-tight text-[#0B0C0E]">Existing services</h2>
          <p className="text-sm text-[#56606E] mt-1">Edit pricing, descriptions, or deactivate services. Inactive services no longer appear on the booking page.</p>
        </div>

        <div className="divide-y divide-[#E5E3DA]">
          {services.map((svc) => {
            const Icon = ICONS[svc.icon] || Plus;
            const dirty = svc._dirty;
            return (
              <div key={svc.id} className="p-6 grid lg:grid-cols-12 gap-4 items-start" data-testid={`service-row-${svc.slug}`}>
                <div className="lg:col-span-1 flex items-center justify-center w-12 h-12 bg-[#0B0C0E] text-[#FACC15]">
                  <Icon className="w-6 h-6" />
                </div>

                <div className="lg:col-span-3">
                  <label className="overline mb-1 block">Name</label>
                  <input value={svc.name} onChange={(e) => updateField(svc.id, "name", e.target.value)}
                    className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
                    data-testid={`svc-name-${svc.slug}`} />
                  <div className="mt-1 text-[10px] uppercase tracking-wider text-[#97A1AE]">slug: {svc.slug}</div>
                </div>

                <div className="lg:col-span-3">
                  <label className="overline mb-1 block">Description</label>
                  <input value={svc.description || ""} onChange={(e) => updateField(svc.id, "description", e.target.value)}
                    className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
                    data-testid={`svc-desc-${svc.slug}`} />
                </div>

                <div className="lg:col-span-2">
                  <label className="overline mb-1 block">Billing</label>
                  <select value={svc.billing_type} onChange={(e) => updateField(svc.id, "billing_type", e.target.value)}
                    className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
                    data-testid={`svc-billing-${svc.slug}`}>
                    <option value="monthly">monthly</option>
                    <option value="on_request">on_request (Discuss)</option>
                  </select>
                </div>

                <div className="lg:col-span-1">
                  <label className="overline mb-1 block">$/mo</label>
                  <input type="number" step="1" disabled={svc.billing_type === "on_request"}
                    value={svc.price_per_month ?? ""}
                    onChange={(e) => updateField(svc.id, "price_per_month", e.target.value)}
                    className="w-full bg-white border border-[#C6C3B5] disabled:bg-[#FCFBF6] disabled:text-[#97A1AE] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
                    data-testid={`svc-price-${svc.slug}`} />
                </div>

                <div className="lg:col-span-1">
                  <label className="overline mb-1 block">Order</label>
                  <input type="number" value={svc.sort_order} onChange={(e) => updateField(svc.id, "sort_order", e.target.value)}
                    className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
                    data-testid={`svc-order-${svc.slug}`} />
                </div>

                <div className="lg:col-span-1 flex flex-col gap-2">
                  <label className="flex items-center gap-2 text-xs text-[#2C2F36] cursor-pointer">
                    <input type="checkbox" checked={!!svc.active} onChange={(e) => updateField(svc.id, "active", e.target.checked)}
                      className="accent-[#0B0C0E]" data-testid={`svc-active-${svc.slug}`} />
                    Active
                  </label>
                </div>

                <div className="lg:col-span-12 flex items-center justify-end gap-3 mt-2">
                  {dirty && <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700">UNSAVED</span>}
                  <button onClick={() => deleteService(svc)} className="text-red-700 hover:bg-red-50 p-2" data-testid={`svc-delete-${svc.slug}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => saveService(svc)} disabled={savingId === svc.id || !dirty}
                    className="bg-[#0B0C0E] hover:bg-black disabled:opacity-40 text-white font-bold px-4 py-2 text-xs uppercase tracking-wide inline-flex items-center gap-2 border-l-4 border-[#FACC15]"
                    data-testid={`svc-save-${svc.slug}`}>
                    {savingId === svc.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
                  </button>
                </div>
              </div>
            );
          })}
          {services.length === 0 && (
            <div className="p-8 text-center text-[#97A1AE] text-sm">No services yet. Add one below.</div>
          )}
        </div>
      </div>

      {/* New service form */}
      <div className="border-2 border-dashed border-[#0B0C0E] bg-[#FCFBF6] p-6" data-testid="new-service-form">
        <div className="overline">Add New Service</div>
        <h3 className="mt-1 font-display font-black text-lg uppercase tracking-tight text-[#0B0C0E]">Create another add-on</h3>

        <div className="mt-5 grid sm:grid-cols-3 gap-4">
          <div>
            <label className="overline mb-1 block">Slug *</label>
            <input value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })}
              placeholder="e.g. water_hookup"
              className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
              data-testid="new-svc-slug" />
            <p className="text-xs text-[#97A1AE] mt-1">Unique identifier (a-z, 0-9, underscores).</p>
          </div>
          <div>
            <label className="overline mb-1 block">Name *</label>
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="Water Hookup"
              className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
              data-testid="new-svc-name" />
          </div>
          <div>
            <label className="overline mb-1 block">Icon</label>
            <select value={draft.icon} onChange={(e) => setDraft({ ...draft, icon: e.target.value })}
              className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
              data-testid="new-svc-icon">
              {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <div className="sm:col-span-3">
            <label className="overline mb-1 block">Description</label>
            <input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              placeholder="Brief description shown on the booking page."
              className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
              data-testid="new-svc-desc" />
          </div>
          <div>
            <label className="overline mb-1 block">Billing Type</label>
            <select value={draft.billing_type} onChange={(e) => setDraft({ ...draft, billing_type: e.target.value })}
              className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
              data-testid="new-svc-billing">
              <option value="monthly">monthly (fixed price)</option>
              <option value="on_request">on_request (Discuss)</option>
            </select>
          </div>
          <div>
            <label className="overline mb-1 block">Price / month ($)</label>
            <input type="number" step="1" disabled={draft.billing_type === "on_request"}
              value={draft.price_per_month}
              onChange={(e) => setDraft({ ...draft, price_per_month: e.target.value })}
              className="w-full bg-white border border-[#C6C3B5] disabled:bg-white disabled:text-[#97A1AE] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
              data-testid="new-svc-price" />
          </div>
          <div>
            <label className="overline mb-1 block">Sort Order</label>
            <input type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })}
              className="w-full bg-white border border-[#C6C3B5] focus:border-[#0B0C0E] outline-none px-3 py-2 text-sm text-[#0B0C0E]"
              data-testid="new-svc-order" />
          </div>
        </div>

        <button onClick={createService} disabled={adding}
          className="mt-6 bg-[#0B0C0E] hover:bg-black disabled:opacity-40 text-white font-bold px-6 py-3 text-xs uppercase tracking-wide inline-flex items-center gap-2 border-l-4 border-[#FACC15]"
          data-testid="new-svc-create">
          {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />} Add Service
        </button>
      </div>
    </div>
  );
}
